import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import api, { earlyAccess, grantEarlyAccess } from "@/lib/api";
import { Sparkles } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { trackSearch } from "@/lib/pixel";
import ProductCard from "@/components/ProductCard";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { PawField } from "@/components/decor";
import { SEASONS, useActiveSeasons } from "@/lib/seasons";
import { useCollectionCounts } from "@/lib/collections";
import ComingSoonTeaser from "@/components/ComingSoonTeaser";

const COLLECTIONS = [
  ["All Products", ""], ["Books", "books"], ["Stickers & Transfers", "stickers-transfers"],
  ["Unisex", "unisex"], ["Kids", "kids"], ["Accessories", "accessories"],
  ["Dog Shop", "dogs"], ["Gifts & Bundles", "gifts"],
];
const AUDIENCES = [["Everyone", ""], ["Adults", "adult"], ["Kids", "kids"], ["Dogs", "dog"]];
const SORTS = [["Featured", "featured"], ["Newest", "newest"], ["Best Selling", "best-selling"],
  ["Price: Low to High", "price-asc"], ["Price: High to Low", "price-desc"]];

function FilterPanel({ filters, setFilter, facets }) {
  const activeSeasons = useActiveSeasons();
  // hide "Made for" chips that have no live products (e.g. the Dog range while it's unavailable)
  const visibleSeasons = SEASONS.filter((s) => activeSeasons.includes(s.slug) || s.slug === filters.seasonal);
  const counts = useCollectionCounts();
  const audiences = AUDIENCES.filter(([, val]) => !val || !counts || (counts[`aud:${val}`] || 0) > 0 || filters.audience === val);
  const visibleCollections = COLLECTIONS.filter(([, val]) => !val || !counts || (counts[val] || 0) > 0 || val === filters.collection);
  const Btn = ({ active, children, onClick, testid }) => (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors border-2 ${active ? "bg-[#FFD93D] border-[#FFD93D] text-[#19324A]" : "bg-white border-[#19324A]/10 text-[#19324A]/70 hover:border-[#FFD93D]"}`}>
      {children}
    </button>
  );
  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-display font-bold text-[#19324A] mb-3">Category</h4>
        <div className="flex flex-wrap gap-2">
          {visibleCollections.map(([label, val]) => (
            <Btn key={label} active={filters.collection === val} onClick={() => setFilter("collection", val)} testid={`filter-collection-${val || "all"}`}>{label}</Btn>
          ))}
        </div>
      </div>
      <div>
        <h4 className="font-display font-bold text-[#19324A] mb-3">Made For</h4>
        <div className="flex flex-wrap gap-2">
          {audiences.map(([label, val]) => (
            <Btn key={label} active={filters.audience === val} onClick={() => setFilter("audience", val)} testid={`filter-audience-${val || "all"}`}>{label}</Btn>
          ))}
        </div>
      </div>
      {visibleSeasons.length > 0 && <div data-testid="filter-seasonal-group">
        <h4 className="font-display font-bold text-[#19324A] mb-3">Seasonal</h4>
        <div className="flex flex-wrap gap-2">
          <Btn active={filters.seasonal === ""} onClick={() => setFilter("seasonal", "")} testid="filter-seasonal-all">Everyday</Btn>
          {visibleSeasons.map((s) => (
            <Btn key={s.slug} active={filters.seasonal === s.slug} onClick={() => setFilter("seasonal", s.slug)} testid={`filter-seasonal-${s.slug}`}>
              {s.emoji} {s.label}{facets.seasonCounts?.[s.slug] ? <span className="ml-1 text-[11px] opacity-60">({facets.seasonCounts[s.slug]})</span> : null}
            </Btn>
          ))}
        </div>
      </div>}
      <div>
        <h4 className="font-display font-bold text-[#19324A] mb-3">Availability</h4>
        <div className="flex flex-wrap gap-2">
          {[["All", ""], ["In Stock", "in-stock"], ["Sold Out", "sold-out"]].map(([label, val]) => (
            <Btn key={label} active={filters.availability === val} onClick={() => setFilter("availability", val)} testid={`filter-avail-${val || "all"}`}>{label}</Btn>
          ))}
        </div>
      </div>
      {facets.sizes.length > 0 && (
        <div>
          <h4 className="font-display font-bold text-[#19324A] mb-3">Size</h4>
          <div className="flex flex-wrap gap-2">
            <Btn active={!filters.size} onClick={() => setFilter("size", "")}>Any</Btn>
            {facets.sizes.map((s) => <Btn key={s} active={filters.size === s} onClick={() => setFilter("size", s)}>{s}</Btn>)}
          </div>
        </div>
      )}
      {facets.colours.length > 0 && (
        <div>
          <h4 className="font-display font-bold text-[#19324A] mb-3">Colour</h4>
          <div className="flex flex-wrap gap-2">
            <Btn active={!filters.colour} onClick={() => setFilter("colour", "")}>Any</Btn>
            {facets.colours.map((c) => <Btn key={c} active={filters.colour === c} onClick={() => setFilter("colour", c)}>{c}</Btn>)}
          </div>
        </div>
      )}
      <div>
        <h4 className="font-display font-bold text-[#19324A] mb-3">Extras</h4>
        <div className="flex flex-wrap gap-2">
          <Btn active={filters.is_new} onClick={() => setFilter("is_new", !filters.is_new)} testid="filter-new">New Arrivals</Btn>
          <Btn active={filters.best_seller} onClick={() => setFilter("best_seller", !filters.best_seller)} testid="filter-best">Best Sellers</Btn>
        </div>
      </div>
    </div>
  );
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  useSeo("Shop All — Bulldog Books, Clothing, Gifts & Dog Merch",
    "Browse the full Chewie & Solo collection: British Bulldog children's books, clothing, stickers, accessories, dog merchandise and gifts.");

  const [filters, setFilters] = useState({
    collection: params.get("collection") || "",
    audience: params.get("audience") || "",
    availability: "",
    size: "", colour: "",
    is_new: params.get("is_new") === "true",
    best_seller: params.get("best_seller") === "true",
    search: params.get("search") || "",
    seasonal: params.get("seasonal") || "",
    sort: "featured",
  });
  const [allForFacets, setAllForFacets] = useState([]);
  const [early, setEarly] = useState(null);
  const [products, setProducts] = useState([]);
  const [offSeason, setOffSeason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setFilters((f) => ({ ...f, collection: params.get("collection") || "", search: params.get("search") || "",
      seasonal: params.get("seasonal") || "",
      is_new: params.get("is_new") === "true", best_seller: params.get("best_seller") === "true" }));
  }, [params]);

  useEffect(() => { api.get("/products?limit=100").then(({ data }) => setAllForFacets(data.products)).catch(() => {}); }, []);

  // Early-access link from the subscriber email: validate, remember the token, then reload the shop with it attached.
  useEffect(() => {
    const slug = params.get("seasonal"), token = params.get("early") || earlyAccess()[slug];
    if (!slug || !token) return;
    api.get(`/seasons/${slug}/early-access?t=${encodeURIComponent(token)}${params.get("early") ? "&first=true" : ""}`).then(({ data }) => {
      if (data.ok) {
        grantEarlyAccess(slug, token);
        setEarly(data);
        if (params.get("early")) { const next = new URLSearchParams(params); next.delete("early"); setParams(next, { replace: true }); }
      } else setEarly({ ...data, expired: true });
    }).catch(() => {});
  }, [params, setParams]);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "" || v === false) return;
      q.set(k, v);
    });
    if (filters.seasonal) q.set("preview", "true");
    api.get(`/products?${q.toString()}`).then(({ data }) => {
      setProducts(data.products);
      if (filters.search) trackSearch(filters.search, data.products);
      setOffSeason(data.off_season ? { returns: data.returns, slug: filters.seasonal } : null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [filters]);

  const returnsLabel = offSeason?.returns ? new Date(offSeason.returns).toLocaleDateString("en-AU", { day: "numeric", month: "short" }) : "soon";
  const seasonMeta = SEASONS.find((s) => s.slug === offSeason?.slug);

  const facets = useMemo(() => ({
    sizes: [...new Set(allForFacets.flatMap((p) => p.sizes || []))],
    colours: [...new Set(allForFacets.flatMap((p) => p.colours || []))],
    seasonCounts: allForFacets.reduce((acc, p) => { if (p.seasonal) acc[p.seasonal] = (acc[p.seasonal] || 0) + 1; return acc; }, {}),
  }), [allForFacets]);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div>
      <section className="relative bg-[#A78BFA]/15 py-10 sm:py-14 overflow-hidden">
        <PawField color="#A78BFA" opacity={0.08} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <span className="font-hand text-2xl text-[#A78BFA]">Have a good sniff around</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-[#19324A]">The Shop</h1>
          {filters.search && <p className="mt-2 text-[#19324A]/70">Results for “{filters.search}”</p>}
          {early?.ok && (
            <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#19324A] text-white px-5 py-2.5 text-sm font-bold soft-shadow" data-testid="early-access-banner">
              <Sparkles className="w-4 h-4 text-[#FFD93D]" /> Early access unlocked — you're shopping {early.emoji} {early.label} before everyone else
            </div>
          )}
          {early?.expired && !early.already_open && (
            <p className="mt-4 text-sm text-[#19324A]/70" data-testid="early-access-expired">That early-access link isn't active yet — Pack members get it the day before launch.</p>
          )}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 flex gap-8">
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-24 bg-white rounded-3xl p-6 soft-shadow"><FilterPanel filters={filters} setFilter={setFilter} facets={facets} /></div>
        </aside>

        <div className="flex-1 min-w-0">
          {!early?.ok && <ComingSoonTeaser />}
          {offSeason && products.length > 0 && (
            <div className="rounded-3xl bg-[#19324A] text-white p-5 sm:p-6 mb-6 flex items-start sm:items-center gap-4 soft-shadow" data-testid="season-preview-banner">
              <span className="text-3xl leading-none">{seasonMeta?.emoji || "✨"}</span>
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#FFD93D]">Sneak peek</p>
                <h3 className="font-display font-extrabold text-xl sm:text-2xl leading-tight text-white" data-testid="season-preview-title">{seasonMeta?.label || "This collection"} opens {returnsLabel}</h3>
                <p className="text-sm text-white/75 mt-1">Here's what's coming — tap any piece for a closer look. Pack members get a 24-hour head start the day before.</p>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <button className="lg:hidden btn-outline !py-2 !px-4 text-sm" data-testid="mobile-filter-btn"><SlidersHorizontal className="w-4 h-4" /> Filters</button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[86vw] max-w-sm bg-[#FFF9EE] overflow-y-auto">
                  <SheetTitle className="sr-only">Filters</SheetTitle>
                  <h3 className="font-display font-bold text-xl text-[#19324A] mb-5">Filters</h3>
                  <FilterPanel filters={filters} setFilter={setFilter} facets={facets} />
                  <button onClick={() => setMobileOpen(false)} className="btn-primary w-full mt-6">Show {products.length} products</button>
                </SheetContent>
              </Sheet>
              <span className="text-[#19324A]/60 text-sm whitespace-nowrap">{products.length} products</span>
            </div>
            <select value={filters.sort} onChange={(e) => setFilter("sort", e.target.value)} data-testid="sort-select"
              className="rounded-full border-2 border-[#19324A]/10 bg-white px-3 sm:px-4 py-2 text-sm font-semibold text-[#19324A] outline-none focus:border-[#FFD93D] max-w-[46%] sm:max-w-none truncate">
              {SORTS.map(([label, val]) => <option key={val} value={val}>{label}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {[...Array(6)].map((_, i) => <div key={i} className="aspect-[3/4] rounded-3xl bg-white/60 animate-pulse" />)}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-display font-bold text-2xl text-[#19324A]">No goodies found here yet.</p>
              <p className="text-[#19324A]/60 mt-2">Try clearing a filter or two. 🐾</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="shop-grid">
              {products.map((p) => <ProductCard key={p.id} product={p} comingSoon={offSeason ? returnsLabel : ""} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
